import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ENTITY_KINDS } from '@mkcore/knowledge/schema';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { formatDate, formatNumber, formatPreferences } from '@/lib/format';
import { dayIn, presetRange } from '@/lib/view';
import { useErrorMessage } from '@/lib/mutation';
import { EntityIcon, KindBadge } from '@/features/knowledge/graph';
import { entitiesQuery, graphStatsQuery, spacesQuery } from '@/features/knowledge/queries';
import { SpaceLabel } from '@/features/knowledge/ui';

const RANGES = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear', 'custom'] as const;

const search = z.object({
  kind: z.enum(ENTITY_KINDS).optional(),
  q: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  space: z.string().optional(),
  sort: z.enum(['name', 'date_asc', 'date_desc']).optional(),
  range: z.enum(RANGES).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

/** The days [from, to] of a range, counted in the zone of the user; a custom range uses the typed days. */
function rangeDays(s: { range?: (typeof RANGES)[number]; from?: string; to?: string }): { from?: string; to?: string } {
  if (!s.range) return {};
  if (s.range === 'custom') return { ...(s.from && { from: s.from }), ...(s.to && { to: s.to }) };
  const { timezone, weekStart } = formatPreferences();
  const today = dayIn(new Date(), timezone);
  if (s.range === 'lastYear') {
    const y = Number(today.slice(0, 4)) - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return presetRange(s.range, today, weekStart) ?? {};
}

export const Route = createFileRoute('/_app/knowledge/entities/')({ validateSearch: search, component: EntitiesPage });

/**
 * The entities of the knowledge graph (CO-1136, CO-1144, CO-1140): people, clients, projects, meetings, topics and
 * decisions found in items and documents, per kind with their counts; a filter on kind, space and words.
 */
function EntitiesPage() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [words, setWords] = useState(s.q ?? '');
  const spaces = useQuery(spacesQuery()).data?.items ?? [];
  const stats = useQuery(graphStatsQuery(s.space ? { spaceId: s.space } : {}));
  const q = useInfiniteQuery(entitiesQuery({ ...(s.kind && { kind: [s.kind] }), ...(s.q && { q: s.q }), ...(s.space && { spaceId: s.space }), ...(s.sort && { sort: s.sort }), ...rangeDays(s), limit: 50 }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const total = (kind: string) => stats.data?.entities.find((x) => x.kind === kind)?.count ?? 0;
  return (
    <>
      <PageHeader eyebrow={<Link to="/knowledge" className="hover:underline">{t('knowledge.title')}</Link>} title={t('knowledge.graph.title')} description={t('knowledge.graph.description')} />
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label={t('knowledge.graph.kind')}>
        <Button size="sm" variant={s.kind ? 'ghost' : 'secondary'} onClick={() => set({ kind: undefined })}>
          {t('knowledge.graph.allKinds')}
        </Button>
        {ENTITY_KINDS.filter((k) => total(k) > 0 || s.kind === k).map((k) => (
          <Button key={k} size="sm" variant={s.kind === k ? 'secondary' : 'ghost'} onClick={() => set({ kind: k })}>
            <EntityIcon kind={k} /> {t(`knowledge.graph.kinds.${k}`)} <span className="text-fg-3">{formatNumber(total(k))}</span>
          </Button>
        ))}
      </div>
      <form
        className="mb-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem_12rem_12rem_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: words.trim() || undefined });
        }}
      >
        <Field label={t('knowledge.graph.search')}>{(p) => <Input {...p} value={words} placeholder={t('knowledge.graph.searchPlaceholder')} onChange={(e) => setWords(e.target.value)} />}</Field>
        <Field label={t('knowledge.item.fields.space')}>
          {(p) => (
            <NativeSelect {...p} value={s.space ?? ''} onChange={(e) => set({ space: e.target.value || undefined })}>
              <option value="">{t('knowledge.allSpaces')}</option>
              {spaces.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('knowledge.graph.date')}>
          {(p) => (
            <NativeSelect {...p} value={s.range ?? ''} onChange={(e) => set({ range: (e.target.value || undefined) as (typeof RANGES)[number] | undefined, ...(e.target.value !== 'custom' && { from: undefined, to: undefined }) })}>
              <option value="">{t('views.anyDate')}</option>
              {RANGES.map((r) => (
                <option key={r} value={r}>
                  {r === 'custom' ? t('knowledge.graph.customRange') : r === 'lastYear' ? t('knowledge.graph.lastYear') : t(`views.date.${r}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('knowledge.graph.sort')}>
          {(p) => (
            <NativeSelect {...p} value={s.sort ?? 'name'} onChange={(e) => set({ sort: e.target.value === 'name' ? undefined : (e.target.value as 'date_asc' | 'date_desc') })}>
              <option value="name">{t('knowledge.graph.sortName')}</option>
              <option value="date_desc">{t('knowledge.graph.sortNewest')}</option>
              <option value="date_asc">{t('knowledge.graph.sortOldest')}</option>
            </NativeSelect>
          )}
        </Field>
        <Button type="submit" variant="secondary">
          {t('knowledge.graph.find')}
        </Button>
      </form>
      {s.range === 'custom' && (
        <div className="mb-4 grid max-w-md grid-cols-2 gap-3">
          <Field label={t('views.minShort')}>{(p) => <Input {...p} type="date" value={s.from ?? ''} max={s.to} onChange={(e) => set({ from: e.target.value || undefined })} />}</Field>
          <Field label={t('views.maxShort')}>{(p) => <Input {...p} type="date" value={s.to ?? ''} min={s.from} onChange={(e) => set({ to: e.target.value || undefined })} />}</Field>
        </div>
      )}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('knowledge.graph.none')} description={t('knowledge.graph.noneHint')} />
      ) : (
        <>
          <ul className="grid gap-2" aria-label={t('knowledge.graph.entities')}>
            {rows.map((e) => (
              <li key={e.id}>
                <Card className="grid gap-1 p-3">
                  <span className="flex flex-wrap items-center gap-2">
                    <EntityIcon kind={e.kind} />
                    <Link to="/knowledge/entities/$id" params={{ id: e.id }} className="font-bold hover:underline">
                      {e.name}
                    </Link>
                    <KindBadge kind={e.kind} />
                    {e.app && <Badge tone="outline">{t(`knowledge.graph.app.${e.app.type}`)}</Badge>}
                  </span>
                  {!!(e.aliases.length || e.emails.length) && <span className="truncate text-sm text-fg-2">{[...e.aliases, ...e.emails].join(', ')}</span>}
                  <span className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
                    <SpaceLabel space={e.space} />
                    <span>{formatDate(e.updatedAt)}</span>
                    <span>{t('knowledge.graph.counts', e.counts)}</span>
                  </span>
                </Card>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {t('common.more')}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
